import { useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { DataTable } from 'components/table/DataTable';
import { Loader } from 'components/Loader';
import { useDebounce } from 'components/Debounce';
import { CustomerColumns } from './CustomerColumns';
import { MainLayout } from 'src/pages/MainLayout';
import { Button } from '@/components/ui/button';
import { useToast } from 'components/useToast';
import { CustomerExport } from 'src/trpc';
import { CustomerSheet } from './CustomerSheet';

const exportCustomers = (customers: CustomerExport[]) => {
  const csvContent = [
    ['Name', 'Email', 'Phone'],
    ...customers.map((customer) => [customer.name, customer.email, customer.phone]),
  ]
    .map((row) => row.join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'customers.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { successToast, errorToast } = useToast();
  const [isCustomerSheetOpen, setIsCustomerSheetOpen] = useState(false);
  const [params, setParams] = useSearchParams({
    offset: '0',
    sortDirection: 'desc',
    sortKey: 'createdAt',
    search: '',
  });
  const offset = parseInt(params.get('offset')!);
  const limit = 25;
  const currentPage = offset === 0 ? 1 : offset / limit + 1;
  const { data, isPending, isFetching } = trpc.admin.customer.adminListCustomers.useQuery(
    {
      limit,
      offset: offset,
      sortDirection: params.get('sortDirection') as 'asc' | 'desc',
      sortKey: params.get('sortKey')!,
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: params.get('sortKey') || 'createdAt',
      desc: (params.get('sortDirection') || 'desc') === 'desc',
    },
  ]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    if (sorting.length) {
      params.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      params.set('sortKey', sorting[0].id);
      params.set('offset', '0');
      setParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  useEffect(() => {
    params.set('search', debouncedSearch || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { mutate, isPending: isExporting } = trpc.admin.customer.adminExportCustomers.useMutation({
    onSuccess: (data: { customers: CustomerExport[] }) => {
      exportCustomers(data.customers);
      successToast({
        title: 'Export Successful',
        description: 'The customer data has been exported successfully.',
      });
    },
    onError: () => {
      errorToast({
        title: 'Export Failed',
        description: 'There was an error exporting the customer data. Please try again.',
      });
    },
  });

  const handleExport = () => {
    mutate({ search: params.get('search') || '' });
  };

  if (isPending && isFetching && !data) {
    return (
      <MainLayout>
        <Loader title="Getting Customers" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-row justify-between">
        <h1 className="flex-1 text-3xl font-bold">Customers</h1>
        <div className="flex gap-2">
          <Button onClick={() => setIsCustomerSheetOpen(true)}>Create Customer</Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Customers'}
          </Button>
        </div>
      </div>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search customer by name, email or phone number"
      />
      <div className="flex flex-col h-full">
        <DataTable
          total={data?.total || 0}
          resourceName="customers"
          offset={offset}
          limit={limit}
          columns={CustomerColumns}
          data={data?.customers || []}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={(data?.total || 0) > offset + limit}
          hasPreviousPage={offset > 0}
          onRowClick={(row) => navigate(`/customers/${row._id}`)}
          onPageChange={(change) => {
            params.set('offset', (offset + change * limit).toString());
            setParams(params);
          }}
          currentPage={currentPage}
          pageCount={Math.ceil((data?.total || 0) / limit)}
        />
      </div>
      <CustomerSheet isOpen={isCustomerSheetOpen} onClose={() => setIsCustomerSheetOpen(false)} />
    </MainLayout>
  );
};
